import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'
import { useAuthStore } from '../../stores/authStore'
import { useAdminStore } from '../../stores/adminStore'
import {
  LayoutDashboard,
  Settings,
  BarChart3,
  FileText,
  FileInput,
  CalendarDays,
  ClipboardList,
  Shield,
  LucideIcon,
} from 'lucide-react'

interface NavItem {
  nameKey: string
  href: string
  icon: LucideIcon
}

const navigation: NavItem[] = [
  { nameKey: 'nav.dashboard', href: '/dashboard', icon: LayoutDashboard },
  { nameKey: 'nav.invoices', href: '/dashboard/invoices', icon: FileText },
  { nameKey: 'nav.incomingInvoices', href: '/dashboard/incoming-invoices', icon: FileInput },
  { nameKey: 'nav.quarterlyReports', href: '/dashboard/quarterly-reports', icon: CalendarDays },
  // { nameKey: 'nav.users', href: '/dashboard/users', icon: Users },
  // { nameKey: 'nav.sales', href: '/dashboard/sales', icon: ShoppingCart },
  // { nameKey: 'nav.products', href: '/dashboard/products', icon: Package },
  { nameKey: 'nav.reports', href: '/dashboard/reports', icon: BarChart3 },
  { nameKey: 'nav.settings', href: '/dashboard/settings', icon: Settings },
]

const adminNavigation: NavItem[] = [
  { nameKey: 'admin.nav.users', href: '/dashboard/admin/users', icon: Shield },
  { nameKey: 'admin.nav.auditLog', href: '/dashboard/admin/audit-log', icon: ClipboardList },
]

export function Sidebar() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role === 'admin'
  const isImpersonating = useAdminStore((s) => s.isImpersonating)

  return (
    <aside className={cn(
      "hidden lg:fixed lg:z-50 lg:flex lg:w-64 lg:flex-col",
      isImpersonating ? "lg:top-10 lg:bottom-0" : "lg:inset-y-0"
    )}>
      <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r bg-card px-6 pb-4">
        {/* Logo */}
        <div className="flex h-16 shrink-0 items-center">
          <span className="text-xl font-bold text-primary">GestorPlus</span>
        </div>

        {/* Navigation */}
        <nav className="flex flex-1 flex-col">
          <ul className="flex flex-1 flex-col gap-y-1">
            {navigation.map((item) => (
              <li key={item.nameKey}>
                <NavLink
                  to={item.href}
                  end={item.href === '/dashboard'}
                  className={({ isActive }) =>
                    cn(
                      'group flex gap-x-3 rounded-md p-2 text-sm font-medium leading-6 transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )
                  }
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  {t(item.nameKey)}
                </NavLink>
              </li>
            ))}

            {/* Admin section */}
            {isAdmin && (
              <>
                <li className="my-2">
                  <div className="border-t" />
                </li>
                <li className="px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t('admin.nav.section')}
                </li>
                {adminNavigation.map((item) => (
                  <li key={item.nameKey}>
                    <NavLink
                      to={item.href}
                      className={({ isActive }) =>
                        cn(
                          'group flex gap-x-3 rounded-md p-2 text-sm font-medium leading-6 transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        )
                      }
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {t(item.nameKey)}
                    </NavLink>
                  </li>
                ))}
              </>
            )}
          </ul>
        </nav>
      </div>
    </aside>
  )
}
